import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningIcon from "@mui/icons-material/Warning";
import { authenticatedFetch } from "../../utils/api";

interface MatchingCompany {
  id: number;
  name: string;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const companySchema = z.object({
  name: z.string().min(1, "Name is required"),
  address: z.string().optional(),
  phone1: z
    .number()
    .int("Phone number must be an integer")
    .optional()
    .nullable(),
  phone2: z
    .number()
    .int("Phone number must be an integer")
    .optional()
    .nullable(),
  gst_number: z.string().optional(),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
});

type CompanyFormData = z.infer<typeof companySchema>;

export default function CreateCompany() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [matchingCompanies, setMatchingCompanies] = useState<MatchingCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const { control, handleSubmit, watch } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      address: "",
      phone1: undefined,
      phone2: undefined,
      gst_number: "",
      email: "",
    },
  });

  const companyName = watch("name");

  // Debounced search for matching companies
  const searchCompanies = useCallback(async (query: string) => {
    if (query.length < 2) {
      setMatchingCompanies([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/companies/search?q=${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const data = await response.json();
        setMatchingCompanies(data);
      }
    } catch (err) {
      console.error("Error searching companies:", err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Effect to trigger search with debounce
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (companyName && companyName.length >= 2) {
      const timeout = setTimeout(() => {
        searchCompanies(companyName);
      }, 300); // 300ms debounce
      setSearchTimeout(timeout);
    } else {
      setMatchingCompanies([]);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [companyName, searchCompanies]);

  const onSubmit = async (data: CompanyFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies`, {
        method: "POST",
        body: JSON.stringify({ company: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      navigate("/companies");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h2">
              Create Company
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/companies")}
              variant="outlined"
            >
              Back
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Company Name"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: isSearching ? (
                        <CircularProgress size={20} />
                      ) : null,
                    }}
                  />
                  {matchingCompanies.length > 0 && (
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        mt: 1, 
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'warning.main',
                        backgroundColor: 'warning.light',
                      }}
                    >
                      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="warning" fontSize="small" />
                        <Typography variant="body2" fontWeight="medium">
                          Similar companies found - please verify this isn't a duplicate:
                        </Typography>
                      </Box>
                      <List dense sx={{ pt: 0 }}>
                        {matchingCompanies.map((company) => (
                          <ListItem key={company.id} disablePadding>
                            <ListItemButton
                              onClick={() => navigate(`/companies/${company.id}`)}
                              sx={{ 
                                py: 0.5,
                                '&:hover': {
                                  backgroundColor: 'warning.dark',
                                  color: 'warning.contrastText',
                                }
                              }}
                            >
                              <ListItemText 
                                primary={company.name}
                                secondary="Click to view existing company"
                                primaryTypographyProps={{ fontWeight: 'medium' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                              />
                              <Chip 
                                label="Existing" 
                                size="small" 
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              )}
            />

            <Controller
              name="address"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Address (Optional)"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="phone1"
              control={control}
              render={({
                field: { onChange, value, ...field },
                fieldState,
              }) => (
                <TextField
                  {...field}
                  value={value || ""}
                  onChange={(e) =>
                    onChange(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  label="Primary Phone (Optional)"
                  type="tel"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="phone2"
              control={control}
              render={({
                field: { onChange, value, ...field },
                fieldState,
              }) => (
                <TextField
                  {...field}
                  value={value || ""}
                  onChange={(e) =>
                    onChange(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  label="Secondary Phone (Optional)"
                  type="tel"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="gst_number"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="GST Number (Optional)"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email (Optional)"
                  type="email"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" fullWidth>
                Create Company
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/companies")}
                fullWidth
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
